import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit'
import { licenseService } from '~/services'
import type { RootState } from '..'
import type { LicenseApiItem } from '~/types/license'

type LicenseState = {
    data: LicenseApiItem[]
    isLoading: boolean
    errorMessage: string
    selectedRowIndex: number
    detail: LicenseApiItem | null
    detailLoading: boolean
    detailErrorMessage: string
    detailRequestId: string
}

const initialState: LicenseState = {
    data: [],
    isLoading: false,
    errorMessage: '',
    selectedRowIndex: -1,
    detail: null,
    detailLoading: false,
    detailErrorMessage: '',
    detailRequestId: '',
}

export const fetchLicenses = createAsyncThunk('license/fetchLicenses', async (_, thunkApi) => {
    try {
        return await licenseService.getLicenses()
    } catch {
        return thunkApi.rejectWithValue('Unable to fetch licenses. Please try again.')
    }
})

export const fetchLicenseDetailById = createAsyncThunk('license/fetchLicenseDetailById', async (id: string, thunkApi) => {
    try {
        return await licenseService.getLicenseById(id)
    } catch {
        return thunkApi.rejectWithValue('Unable to load license details. Please try again.')
    }
})

const licenseSlice = createSlice({
    name: 'license',
    initialState,
    reducers: {
        setSelectedRowIndex: (state, action: PayloadAction<number>) => {
            state.selectedRowIndex = action.payload
        },
        clearLicenseError: (state) => {
            state.errorMessage = ''
        },
        clearLicenseDetail: (state) => {
            state.detail = null
            state.detailErrorMessage = ''
            state.detailLoading = false
            state.detailRequestId = ''
        },
        markLicenseDeleting: (state, action: PayloadAction<string>) => {
            const licenseID = action.payload
            state.data = state.data.map((license) => {
                if (license.id !== licenseID) {
                    return license
                }
                return {
                    ...license,
                    status: 'DELETING',
                    runtimeInfo: {
                        ...license.runtimeInfo,
                        syncStatus: license.runtimeInfo.syncStatus.map((syncStatus) => ({
                            ...syncStatus,
                            status: syncStatus.status?.toUpperCase() === 'SYNCED' ? 'SYNCED' : 'DELETE_PENDING',
                        })),
                    },
                }
            })
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchLicenses.pending, (state) => {
                if (state.data.length === 0) {
                    state.isLoading = true
                }
                state.errorMessage = ''
            })
            .addCase(fetchLicenses.fulfilled, (state, action) => {
                const selectedLicenseId = state.selectedRowIndex >= 0
                    ? state.data[state.selectedRowIndex]?.id
                    : undefined
                state.data = Array.isArray(action.payload) ? action.payload : []
                state.selectedRowIndex = selectedLicenseId
                    ? state.data.findIndex((license) => license.id === selectedLicenseId)
                    : -1
                state.isLoading = false
            })
            .addCase(fetchLicenses.rejected, (state, action) => {
                state.data = []
                state.selectedRowIndex = -1
                state.isLoading = false
                state.errorMessage = (action.payload as string) || action.error.message || 'Unable to fetch licenses. Please try again.'
            })
            .addCase(fetchLicenseDetailById.pending, (state, action) => {
                state.detailLoading = true
                state.detailErrorMessage = ''
                state.detail = null
                state.detailRequestId = action.meta.requestId
            })
            .addCase(fetchLicenseDetailById.fulfilled, (state, action) => {
                if (state.detailRequestId !== action.meta.requestId) {
                    return
                }
                state.detailLoading = false
                state.detail = action.payload
                state.detailRequestId = ''
            })
            .addCase(fetchLicenseDetailById.rejected, (state, action) => {
                if (state.detailRequestId !== action.meta.requestId) {
                    return
                }
                state.detailLoading = false
                state.detail = null
                state.detailRequestId = ''
                state.detailErrorMessage = (action.payload as string) || action.error.message || 'Unable to load license details. Please try again.'
            })
    },
})

export const licenseActions = licenseSlice.actions
export default licenseSlice.reducer

export const licenseData = (state: RootState) => state.license.data
export const licenseLoading = (state: RootState) => state.license.isLoading
export const licenseErrorMessage = (state: RootState) => state.license.errorMessage
export const licenseSelectedRowIndex = (state: RootState) => state.license.selectedRowIndex
export const licenseDetail = (state: RootState) => state.license.detail
export const licenseDetailLoading = (state: RootState) => state.license.detailLoading
export const licenseDetailErrorMessage = (state: RootState) => state.license.detailErrorMessage
